import AppShell from "@/components/AppShell";
import DeleteConfirm from "@/components/DeleteConfirm";
import DetailsPanel from "@/components/DetailsPanel";
import { buildListOptions } from "@/lib/listOptions";
import EmptyState from "@/components/EmptyState";
import PageHeader from "@/components/PageHeader";
import type { SearchOrigin } from "@/components/SearchLink";
import TaskContextMenu, { type MenuPatch } from "@/components/TaskContextMenu";
import TaskRow, { type TaskData } from "@/components/TaskRow";
import {
  toggleTask as mutateToggle,
  deleteTask as mutateDelete,
  updateTask,
  bulkPatchTasks,
  bulkDeleteTasks,
} from "@/mutations/tasks";
import { useTaskSelection } from "@/hooks/useTaskSelection";
import { useLeavingRows } from "@/hooks/useLeavingRows";
import { useLocalTasks } from "@/hooks/useLocalTasks";
import { taskCount } from "@/lib/plural";
import type { BucketKind, SharedProps } from "@/types";
import { router, usePage } from "@inertiajs/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { PressPoint } from "@/hooks/useLongPress";

interface Props {
  q: string;
  bucket: BucketKind | null;
  from?: SearchOrigin | null;
  tasks: TaskData[];
  fromIds?: string[];
  truncated: boolean;
}

interface Filters {
  q: string;
  bucket: BucketKind | null;
  from: SearchOrigin | null;
}

const PARTIAL_PROPS = ["q", "bucket", "from", "tasks", "fromIds", "truncated", "fetchedAt"];

const NO_IDS: string[] = [];

const SMART_ORIGIN_NAMES: Record<string, string> = { my_day: "Мой день", planned: "Запланировано" };

function visit({ q, bucket, from }: Filters) {
  const params: Record<string, string> = {};
  if (q) params.q = q;
  if (bucket) params.bucket = bucket;
  if (from) params.from = from;
  router.get("/search", params, { preserveState: true, preserveScroll: true, replace: true, only: PARTIAL_PROPS });
}

function chipClass(active: boolean): string {
  return `px-2 py-0.5 rounded-md text-xs cursor-pointer ${
    active
      ? "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
      : "text-gray-500 bg-gray-50 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
  }`;
}

// Long enough that typing a word does not fire a request per keystroke, short enough
// that results feel like they follow the cursor.
const DEBOUNCE_MS = 250;

const MAGNIFIER = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 110-16 8 8 0 010 16z" />
  </svg>
);

export default function SearchShow({ q, bucket, from = null, tasks, fromIds = NO_IDS, truncated }: Props) {
  const sidebar = usePage<SharedProps>().props.sidebar;
  const [query, setQuery] = useState(q);
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const [taskPendingDelete, setTaskPendingDelete] = useState<TaskData | null>(null);
  const [bulkDeletePending, setBulkDeletePending] = useState<TaskData[] | null>(null);
  const [contextMenu, setContextMenu] = useState<{ tasks: TaskData[]; x: number; y: number } | null>(null);

  // The query the server props describe. Typing is debounced against it, so a visit
  // that lands with the same text (back/forward, a refresh) does not re-fire.
  const serverQuery = useRef(q);
  serverQuery.current = q;

  useEffect(() => {
    const next = query.trim();
    if (next === serverQuery.current) return;
    const timer = window.setTimeout(() => {
      visit({ q: next, bucket, from });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, bucket, from]);

  const listOptions = buildListOptions(sidebar);

  // The server picks the results and their order; each one shows as the local copy has it.
  const local = useLocalTasks("search", tasks.map((t) => t.id));
  const shownTasks = useMemo(() => {
    if (!local) return tasks;
    return tasks
      .filter((t) => !local.deleted.has(t.id))
      .map((t) => (local.rows[t.id] ? ({ ...t, ...local.rows[t.id] } as TaskData) : t));
  }, [tasks, local]);
  const visibleTasks = useLeavingRows(shownTasks);

  const originName = from
    ? (SMART_ORIGIN_NAMES[from] ?? listOptions.find((option) => option.kind === from)?.name ?? null)
    : null;

  const groups = useMemo(() => {
    if (!from) return [{ title: null, tasks: visibleTasks }];
    const originIds = new Set(fromIds);
    return [
      { title: originName, tasks: visibleTasks.filter((t) => originIds.has(t.id)) },
      { title: "Остальные", tasks: visibleTasks.filter((t) => !originIds.has(t.id)) },
    ].filter((group) => group.tasks.length > 0);
  }, [from, fromIds, visibleTasks, originName]);

  const selectableIds = useMemo(() => groups.flatMap((g) => g.tasks.map((t) => t.id)), [groups]);
  const taskById = useMemo(() => new Map(visibleTasks.map((t) => [t.id, t])), [visibleTasks]);
  const selection = useTaskSelection(selectableIds);

  useEffect(() => {
    if (!selectedTask) return;
    if ((selectedTask.parentId ?? null) !== null) return;
    const fresh = visibleTasks.find((t) => t.id === selectedTask.id);
    if (!fresh) setSelectedTask(null);
    else if (fresh !== selectedTask) setSelectedTask(fresh);
  }, [visibleTasks, selectedTask]);

  function toggleBucket(kind: BucketKind) {
    visit({ q: query.trim(), bucket: bucket === kind ? null : kind, from });
  }

  function openContextMenu(point: PressPoint, task: TaskData) {
    const ids = selection.selectForContext(task.id);
    const picked = ids.map((id) => taskById.get(id)).filter((t): t is TaskData => !!t);
    setContextMenu({ tasks: picked.length ? picked : [task], x: point.clientX, y: point.clientY });
  }

  function selectRow(task: TaskData, e: React.MouseEvent) {
    const intent = selection.handleClick(task.id, e);
    setSelectedTask(intent === "open" ? task : null);
  }

  function applyMenuPatch(payload: MenuPatch) {
    if (!contextMenu) return;
    const ids = contextMenu.tasks.map((t) => t.id);
    if (ids.length === 1) updateTask(ids[0], payload);
    else bulkPatchTasks(ids, payload);
    selection.clear();
  }

  function confirmBulkDelete() {
    if (!bulkDeletePending) return;
    bulkDeleteTasks(bulkDeletePending.map((t) => t.id));
    selection.clear();
    setBulkDeletePending(null);
  }

  function confirmDeleteTask() {
    if (!taskPendingDelete) return;
    mutateDelete(taskPendingDelete.id);
    setTaskPendingDelete(null);
  }

  return (
    <AppShell>
      <PageHeader title="Поиск" />

      <div className="px-5 md:px-7">
        <input
          autoFocus
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Название или заметка…"
          aria-label="Поиск задач"
          className="w-full text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 text-gray-900 dark:text-gray-100 placeholder-gray-400"
        />

        <div className="mt-3 flex flex-wrap gap-1.5" role="group" aria-label="Фильтры">
          {listOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={bucket === option.kind}
              onClick={() => toggleBucket(option.kind)}
              className={chipClass(bucket === option.kind)}
            >
              {option.name}
            </button>
          ))}
        </div>

        {q === "" && !bucket ? (
          <EmptyState icon={MAGNIFIER} text="Начни печатать — ищем по названиям и заметкам." />
        ) : visibleTasks.length === 0 && q === "" ? (
          <EmptyState icon={MAGNIFIER} text="Здесь пусто — всё разобрано ✨" />
        ) : visibleTasks.length === 0 ? (
          <EmptyState icon={MAGNIFIER} text="Ничего не нашлось — попробуй другой запрос или сними фильтр." />
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.title ?? "all"} className="mt-4">
                {group.title && (
                  <h3 className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                    {group.title}
                  </h3>
                )}
                <div
                  className="-mx-2 md:-mx-4 space-y-1.5"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) selection.clear();
                  }}
                >
                  {group.tasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      onToggle={(t) => mutateToggle(t)}
                      onRequestDelete={setTaskPendingDelete}
                      onDelete={(t) => mutateDelete(t.id)}
                      onSelect={selectRow}
                      onContextMenu={openContextMenu}
                      selected={selection.isSelected(task.id)}
                    />
                  ))}
                </div>
              </section>
            ))}
            {truncated && (
              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                Показаны первые {taskCount(tasks.length)} — уточните запрос.
              </p>
            )}
          </>
        )}
      </div>

      {taskPendingDelete && (
        <DeleteConfirm
          title={`Удалить «${taskPendingDelete.title}»?`}
          body="Задача будет удалена безвозвратно."
          onConfirm={confirmDeleteTask}
          onCancel={() => setTaskPendingDelete(null)}
        />
      )}

      {selectedTask && (
        <DetailsPanel
          task={selectedTask}
          lists={listOptions}
          onClose={() => setSelectedTask(null)}
          onDelete={(t) => {
            setSelectedTask(null);
            setTaskPendingDelete(t);
          }}
        />
      )}

      {bulkDeletePending && (
        <DeleteConfirm
          title={
            bulkDeletePending.length === 1
              ? `Удалить «${bulkDeletePending[0].title}»?`
              : `Удалить ${taskCount(bulkDeletePending.length)}?`
          }
          body={
            bulkDeletePending.length === 1
              ? "Задача будет удалена безвозвратно."
              : "Задачи будут удалены безвозвратно."
          }
          onConfirm={confirmBulkDelete}
          onCancel={() => setBulkDeletePending(null)}
        />
      )}

      {contextMenu && (
        <TaskContextMenu
          tasks={contextMenu.tasks}
          lists={listOptions}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onPatch={applyMenuPatch}
          onDelete={() => setBulkDeletePending(contextMenu.tasks)}
        />
      )}
    </AppShell>
  );
}
